/**
 * Search-oriented titles and descriptions for each County Record Hub.
 *
 * Titles use the name people actually type for that county's records office
 * (Recorder, County Clerk, Register of Deeds, Official Records, ACRIS, GSCCCA)
 * plus the county and state. Descriptions lead with the same search phrase and
 * then state only facts already recorded for the county's own portals.
 */
export interface CountyRecordHubCopy {
  title: string;
  description: string;
}

export const COUNTY_RECORD_HUB_COPY: Record<string, CountyRecordHubCopy> = {
  "nd-dunn": {
    title: "Dunn County, ND Recorder Search & Property Records",
    description:
      "Search Dunn County, North Dakota recorder records, parcels and section-line right-of-way. Parcel and ROW layers are open ArcGIS services; recorded instruments require the county portal.",
  },
  "az-maricopa": {
    title: "Maricopa County, AZ Recorder Search & Easement Records",
    description:
      "Search Maricopa County, Arizona recorder documents, parcels, plats and recorded easements. Assessor parcels, subdivision plats and easement documents are all queryable without a subscription.",
  },
  "tx-travis": {
    title: "Travis County, TX County Clerk Records Search",
    description:
      "Search Travis County, Texas County Clerk records and parcel maps. Parcel layers are open; deed images sit behind the clerk portal, and some TxDOT right-of-way maps need a records request.",
  },
  "nv-clark": {
    title: "Clark County, NV Recorder Search & Property Records",
    description:
      "Search Clark County, Nevada recorder records, assessor parcel maps and recorded plat easements. Open GISMO services expose them; utility corridors on BLM-adjacent land are only partly digitized.",
  },
  "mi-kalamazoo": {
    title: "Kalamazoo County, MI Register of Deeds Search",
    description:
      "Find Kalamazoo County, Michigan Register of Deeds records. Only a legacy search portal is online with no public REST service; parcel exports and easement lists come by FOIA request, typically within five business days.",
  },
  "sc-beaufort": {
    title: "Beaufort County, SC Register of Deeds & Deed Records",
    description:
      "Search Beaufort County, South Carolina deed records and parcel maps. Parcels, road right-of-way centerlines and OCRM critical-line layers are public, and deed images are indexed from 1785 forward.",
  },
  "ca-los-angeles": {
    title: "Los Angeles County, CA Recorder Search & Property Records",
    description:
      "Search Los Angeles County, California recorded documents through the Registrar-Recorder. Copies are ordered rather than viewed free online; parcel maps come from the Assessor portal.",
  },
  "il-cook": {
    title: "Cook County, IL Recorder of Deeds & Clerk Recordings Search",
    description:
      "Search Cook County, Illinois deeds, easements and plats by PIN or name in the Clerk's Recordings Search. The county GIS viewer shows parcels and recorded subdivisions.",
  },
  "tx-harris": {
    title: "Harris County, TX County Clerk Real Property Records Search",
    description:
      "Search Harris County, Texas real property records, recorded easements and plats through the County Clerk. Parcel boundaries come from the appraisal district map; the county's parcel service lists about 1.55 million parcels.",
  },
  "ca-san-diego": {
    title: "San Diego County, CA Recorder Search & Official Records",
    description:
      "Search San Diego County, California official records through the Assessor/Recorder/County Clerk. The grantor/grantee index is online and document images are ordered; SanGIS publishes parcel and easement map data.",
  },
  "ca-orange": {
    title: "Orange County, CA Clerk-Recorder Search & Official Records",
    description:
      "Search Orange County, California recorded documents through the OC Clerk-Recorder's online index. County GIS hosts parcel and survey records.",
  },
  "fl-miami-dade": {
    title: "Miami-Dade County, FL Official Records Search",
    description:
      "Search Miami-Dade County, Florida official records, including easements and plats, through the Clerk (free registration). County GIS shows parcels and right-of-way, and its plat service lists 4,304 recorded plats.",
  },
  "tx-dallas": {
    title: "Dallas County, TX County Clerk Records Search",
    description:
      "Search Dallas County, Texas recorded documents through the County Clerk recording office, which explains the online record search. Parcel maps come from the Dallas Central Appraisal District.",
  },
  "ny-kings": {
    title: "Kings County (Brooklyn), NY Property Records Search (ACRIS)",
    description:
      "Search Brooklyn deeds and easements in NYC ACRIS, which has free images of recorded deeds and easements for Brooklyn, Manhattan, Queens and the Bronx. ZoLa shows tax lots and zoning.",
  },
  "wa-king": {
    title: "King County, WA Recorder's Office Records Search",
    description:
      "Search King County, Washington recorded documents through the Recorder's Office, with free document images. The Parcel Viewer shows lots and recorded plats; the county parcel service lists 639,284 parcels.",
  },
  "ca-riverside": {
    title: "Riverside County, CA Recorder Search & Official Records",
    description:
      "Search Riverside County, California recorded documents through the Assessor-County Clerk-Recorder. The index is online and copies are ordered; the county's Map My County GIS shows parcels and easements.",
  },
  "tx-tarrant": {
    title: "Tarrant County, TX County Clerk Records Search",
    description:
      "Search Tarrant County, Texas real property records through the County Clerk's online record search. Parcels come from the Tarrant Appraisal District map.",
  },
  "tx-bexar": {
    title: "Bexar County, TX County Clerk Records Search",
    description:
      "Search Bexar County, Texas recorded easements and plats with document images in the County Clerk's public search. Parcels come from the Bexar Appraisal District.",
  },
  "fl-broward": {
    title: "Broward County, FL Official Records Search",
    description:
      "Search Broward County, Florida official records with free images. The Broward Property Appraiser map shows parcels and plats.",
  },
  "ca-santa-clara": {
    title: "Santa Clara County, CA Recorder Search & Official Records",
    description:
      "Search Santa Clara County, California recorded documents through the Clerk-Recorder's Office. The index is online and copies are ordered; Assessor maps show parcels.",
  },
  "mi-wayne": {
    title: "Wayne County, MI Register of Deeds Records Search",
    description:
      "Search Wayne County, Michigan Register of Deeds land records online (some features are paid). Parcel data is held by local assessors.",
  },
  "ga-fulton": {
    title: "Fulton County, GA Deed Records Search (GSCCCA)",
    description:
      "Search Fulton County, Georgia deeds and plats through the statewide GSCCCA index (subscription for images). Fulton County GIS shows parcels.",
  },
  "mn-hennepin": {
    title: "Hennepin County, MN Property Records Search",
    description:
      "Search Hennepin County, Minnesota property records, including abstract and Torrens documents. The county Property Map shows parcels.",
  },
  "oh-franklin": {
    title: "Franklin County, OH Recorder Search & Deed Records",
    description:
      "Search Franklin County, Ohio deed records with the Recorder's free online document search. The County Auditor's site shows parcels and maps.",
  },
  "nc-mecklenburg": {
    title: "Mecklenburg County, NC Register of Deeds Search",
    description:
      "Search Mecklenburg County, North Carolina deeds with free online images from the Register of Deeds. Polaris 3G shows parcels, plats and easements.",
  },
  "il-dupage": {
    title: "DuPage County, IL Clerk Recordings & Right-of-Way Map Search",
    description:
      "Search DuPage County, Illinois recorded documents through the County Clerk, plus 5,940 right-of-way polygons in the county's open ROW map layer.",
  },
  "fl-sarasota": {
    title: "Sarasota County, FL Official Records & Easement Map Search",
    description:
      "Search Sarasota County, Florida official records from the Clerk, plus 5,384 recorded easement polygons in the county's open GIS layer.",
  },
  "mn-ramsey": {
    title: "Ramsey County, MN Property Records & Easement Map Search",
    description:
      "Search Ramsey County, Minnesota abstract and Torrens property records, plus 13,449 easement polygons in the county's open data service.",
  },
  "va-fairfax": {
    title: "Fairfax County, VA Land Records (CPAN) & Easement Search",
    description:
      "Search Fairfax County, Virginia land records through the Circuit Court's CPAN, plus 50,116 stormwater easements with deed book and page.",
  },
  "fl-leon": {
    title: "Leon County, FL Official Records & Easement Map Search",
    description:
      "Search Leon County, Florida official records from the Clerk, plus 18,144 easements in the TLCGIS layer tied to record book and page.",
  },
  "va-chesterfield": {
    title: "Chesterfield County, VA Land Records & Easement Search",
    description:
      "Search Chesterfield County, Virginia Circuit Court land records online, plus 1,883 ingress/egress easements in the county's open GIS.",
  },
  "pa-allegheny": {
    title: "Allegheny County, PA Department of Real Estate & Parcel Map Search",
    description:
      "Search Allegheny County, Pennsylvania recorded documents through the Allegheny Department of Real Estate, plus 580,039 parcel polygons in the county's open map service.",
  },
  "fl-pinellas": {
    title: "Pinellas County, FL Clerk Official Records & Easement Map Search",
    description:
      "Search Pinellas County, Florida recorded documents through the Pinellas Clerk Official Records, plus 3,169 easement polygons in the county's open map service.",
  },
  "tx-denton": {
    title: "Denton County, TX Public Search & Parcel Map Search",
    description:
      "Search Denton County, Texas recorded documents through the Denton County Public Search, plus 304,723 parcel polygons in the county's open map service.",
  },
  "ga-dekalb": {
    title: "DeKalb County, GA GSCCCA Statewide Search & Parcel Map Search",
    description:
      "Search DeKalb County, Georgia recorded documents through the GSCCCA Statewide Search, plus 246,205 parcel polygons in the county's open map service.",
  },
  "ca-contra-costa": {
    title: "Contra Costa County, CA Clerk-Recorder & Parcel Map Search",
    description:
      "Search Contra Costa County, California recorded documents through the Contra Costa Clerk-Recorder, plus 387,860 parcel polygons in the county's open map service.",
  },
  "la-east-baton-rouge": {
    title: "East Baton Rouge Parish, LA EBR Clerk of Court Records & Parcel Map Search",
    description:
      "Search East Baton Rouge Parish, Louisiana recorded documents through the EBR Clerk of Court Records, plus 198,352 parcel polygons in the county's open map service.",
  },
  "fl-lee": {
    title: "Lee County, FL Clerk Official Records & Parcel Map Search",
    description:
      "Search Lee County, Florida recorded documents through the Lee Clerk Official Records, plus 564,734 parcel polygons in the county's open map service.",
  },
  "mi-kent": {
    title: "Kent County, MI Online Deeds Search & Parcel Map Search",
    description:
      "Search Kent County, Michigan recorded documents through the Kent County Online Deeds Search, plus 232,075 parcel polygons in the county's open map service.",
  },
  "ny-erie": {
    title: "Erie County, NY Clerk Records & Parcel Map Search",
    description:
      "Search Erie County, New York recorded documents through the Erie County Clerk Records, plus 360,170 parcel polygons in the county's open map service.",
  },
  "pa-montgomery": {
    title: "Montgomery County, PA Recorder of Deeds & Parcel Map Search",
    description:
      "Search Montgomery County, Pennsylvania recorded documents through the Montgomery County Recorder of Deeds, plus 310,134 parcel polygons in the county's open map service.",
  },
  "hi-honolulu": {
    title: "Honolulu County, HI Hawaii Bureau of Conveyances & Easement Map Search",
    description:
      "Search Honolulu County, Hawaii recorded documents through the Hawaii Bureau of Conveyances, plus 9,902 easement polygons in the county's open map service.",
  },
  "tx-el-paso": {
    title: "El Paso County, TX Official Public Records & Parcel Map Search",
    description:
      "Search El Paso County, Texas recorded documents through the El Paso County Official Public Records, plus 391,659 parcel polygons in the county's open map service.",
  },
  "wi-milwaukee": {
    title: "Milwaukee County, WI Register of Deeds & Parcel Map Search",
    description:
      "Search Milwaukee County, Wisconsin recorded documents through the Milwaukee Register of Deeds, plus 280,670 parcel polygons in the county's open map service.",
  },
  "co-denver": {
    title: "Denver County, CO Clerk and Recorder & Parcel Map Search",
    description:
      "Search Denver County, Colorado recorded documents through the Denver Clerk and Recorder, plus 240,437 parcel polygons in the county's open map service.",
  },
  "wa-pierce": {
    title: "Pierce County, WA Auditor Recording & Parcel Map Search",
    description:
      "Search Pierce County, Washington recorded documents through the Pierce County Auditor Recording, plus 339,999 parcel polygons in the county's open map service.",
  },
  "wa-snohomish": {
    title: "Snohomish County, WA Auditor Recording & Parcel Map Search",
    description:
      "Search Snohomish County, Washington recorded documents through the Snohomish County Auditor Recording, plus 370,392 parcel polygons in the county's open map service.",
  },
  "ga-gwinnett": {
    title: "Gwinnett County, GA GSCCCA Statewide Search & Parcel Map Search",
    description:
      "Search Gwinnett County, Georgia recorded documents through the GSCCCA Statewide Search, plus 309,818 parcel polygons in the county's open map service.",
  },
  "ca-alameda": {
    title: "Alameda County, CA Clerk-Recorder & Parcel Map Search",
    description:
      "Search Alameda County, California recorded documents through the Alameda County Clerk-Recorder, plus 489,636 parcel polygons in the county's open map service.",
  },
  "nv-washoe": {
    title: "Washoe County, NV Recorder & Parcel Map Search",
    description:
      "Search Washoe County, Nevada recorded documents through the Washoe County Recorder, plus 194,122 parcel polygons in the county's open map service.",
  },
  "id-ada": {
    title: "Ada County, ID Clerk Property Records & Parcel Map Search",
    description:
      "Search Ada County, Idaho recorded documents through the Ada County Clerk Property Records, plus 240,373 parcel polygons in the county's open map service.",
  },
  "oh-hamilton": {
    title: "Hamilton County, OH Recorder & Parcel Map Search",
    description:
      "Search Hamilton County, Ohio recorded documents through the Hamilton County Recorder, plus 418,487 parcel polygons in the county's open map service.",
  },
  "ks-sedgwick": {
    title: "Sedgwick County, KS Register of Deeds & Plat Map Search",
    description:
      "Search Sedgwick County, Kansas recorded documents through the Sedgwick County Register of Deeds, plus 8,340 recorded plat polygons in the county's open map service.",
  },
  "mi-oakland": {
    title: "Oakland County, MI Register of Deeds & Parcel Map Search",
    description:
      "Search Oakland County, Michigan recorded documents through the Oakland County Register of Deeds, plus 490,640 parcel polygons in the county's open map service.",
  },
  "va-loudoun": {
    title: "Loudoun County, VA Circuit Court Land Records & Parcel Map Search",
    description:
      "Search Loudoun County, Virginia recorded documents through the Loudoun Circuit Court Land Records, plus 132,898 parcel polygons in the county's open map service.",
  },
  "fl-polk": {
    title: "Polk County, FL Clerk Official Records & Parcel Map Search",
    description:
      "Search Polk County, Florida recorded documents through the Polk Clerk Official Records, plus 438,616 parcel polygons in the county's open map service.",
  },
};
